<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="sizeClass"
      :style="{ '--pin-active-color': 'var(--device-color-switch)' }"
      :title="device.deviceName">
      <!-- The drawn server inside the status ring (P14): online fills the
           family's green ring, offline keeps the track. -->
      <ControlStatusRing :value="online ? 100 : 0">
        <ControlIcon name="server" :size="pinIconSize" :style="online ? { color: 'var(--device-color-switch)' } : {}" />
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="deviceIcon"
      :name="displayName"
      :is-on="isOn"
      active-color="amber-400"
      @toggle="toggleEnabled"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <!-- No head of its own: the sheet's chrome paints the circle, the name and
       the online pill (card t_488df70b); the body starts at the control. -->
  <template v-else>
    <div class="network-sheet">
      <!-- The one control — enable/disable: the same `enabled` write the old
           pill made, in the sheet's toggle row. -->
      <ControlToggleRow
        :model-value="isOn"
        :disabled="controlling"
        icon="power"
        :label="isOn ? tt('Enabled', 'Omogočeno') : tt('Disabled', 'Onemogočeno')"
        @update:model-value="toggleEnabled" />

      <!-- The readouts, one tile each: clients, CPU and memory (the old
           metric row), temperature, uptime, firmware and the IP. -->
      <div class="network-grid">
        <ControlValueTile
          v-for="metric in usageMetrics"
          :key="metric.key"
          :icon="metric.icon"
          :label="metric.name"
          :value="metric.display" />
        <ControlValueTile
          v-if="temperatureValue !== null"
          icon="thermometer"
          :label="tt('Temperature', 'Temperatura')"
          :value="temperatureDisplay" />
        <ControlValueTile icon="clock" :label="tt('Uptime', 'Čas delovanja')" :value="uptimeDisplay" />
        <ControlValueTile v-if="firmwareValue" icon="computer" label="FW" :value="firmwareValue" />
        <ControlValueTile v-if="ipValue" icon="pin" label="IP" :value="ipValue" />
      </div>

      <p v-if="lastUpdatedLabel" class="network-meta">
        <ControlIcon name="clock" size="0.8125rem" />
        {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdatedLabel }}
      </p>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { formatTime } from '../../utils/formatDate';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import ControlToggleRow from '../device-sheet/ControlToggleRow.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── Live values ──────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

function valueDef(key: string): DeviceValueWithReferences | undefined {
  return (props.device.deviceValues ?? []).find((v) => v.valueKey === key);
}

function readValue(key: string): unknown {
  if (key in currentValues.value) return currentValues.value[key];
  return valueDef(key)?.currentValue ?? undefined;
}

const onlineState = computed<boolean | undefined>(() => {
  const v = readValue('online');
  if (v === true || v === 1 || v === 'true' || v === 'on') return true;
  if (v === false || v === 0 || v === 'false' || v === 'off') return false;
  return undefined;
});
const online = computed<boolean>(() => onlineState.value === true);

const enabledState = computed<boolean>(() => {
  const v = readValue('enabled');
  return v === true || v === 1 || v === 'true' || v === 'on';
});
const isOn = computed<boolean>(() => enabledState.value);

function toNumber(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

const clientsValue = computed<number | null>(() => toNumber(readValue('clients')));
const cpuValue = computed<number | null>(() => toNumber(readValue('cpu')));
const memValue = computed<number | null>(() => toNumber(readValue('mem')));
const temperatureValue = computed<number | null>(() => toNumber(readValue('temperature')));
const uptimeSeconds = computed<number | null>(() => toNumber(readValue('uptime_seconds')));
const firmwareValue = computed<string | null>(() => {
  const v = readValue('firmware');
  return v ? String(v) : null;
});
const ipValue = computed<string | null>(() => {
  const v = readValue('ip');
  return v ? String(v) : null;
});

const usageMetrics = computed(() => {
  const label = (value: number | null, unit: string) =>
    value === null ? '—' : `${Math.round(value * 10) / 10}${unit}`;
  return [
    {
      key: 'clients',
      name: tt('Clients', 'Odjemalci'),
      icon: 'person',
      display: clientsValue.value === null ? '—' : String(clientsValue.value),
    },
    { key: 'cpu', name: 'CPU', icon: 'activity', display: label(cpuValue.value, '%') },
    {
      key: 'mem',
      name: tt('Memory', 'Pomnilnik'),
      icon: 'server',
      display: label(memValue.value, '%'),
    },
  ];
});

const temperatureDisplay = computed(() => {
  if (temperatureValue.value === null) return '—';
  return `${temperatureValue.value.toFixed(0)} °C`;
});

const uptimeDisplay = computed(() => {
  const seconds = uptimeSeconds.value;
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
});

const lastUpdatedLabel = computed(() => {
  let latest: number | null = null;
  for (const v of props.device.deviceValues ?? []) {
    const ts = v.lastUpdated || v.updatedAt;
    if (!ts) continue;
    const t = new Date(ts).getTime();
    if (Number.isFinite(t) && (latest === null || t > latest)) latest = t;
  }
  if (!latest) return '';
  return formatTime(new Date(latest));
});

// ── Display helpers ──────────────────────────────────────────────────────
const deviceIcon = computed<string>(() => {
  const stored = props.device.icon;
  if (stored) return stored === 'wifi_access_point' ? 'wifi' : stored;
  switch (props.device.deviceType || props.device.deviceControl || '') {
    case 'unifi_access_point':
      return 'wifi';
    case 'unifi_switch':
      return 'lan';
    case 'unifi_gateway':
      return 'router';
    default:
      return 'router';
  }
});

const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);

const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn server's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

// ── Actions ──────────────────────────────────────────────────────────────
async function toggleEnabled() {
  if (controlling.value) return;
  await setProperty(props.device, 'enabled', !isOn.value);
}
</script>

<style scoped>
/* Control mode: the readouts in the sheet language. The host chrome owns the
   head and the panel; this block is the toggle row, the tile grid and the
   meta line. */
.network-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.875rem * var(--ui-space-factor));
}

.network-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

@media (min-width: 640px) {
  .network-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.network-meta {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375em;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

.dark .network-meta {
  color: rgb(var(--gray-400));
}
</style>
